<template>
  <div class="pc-select" data-cy="pc-select-modal">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="pc-select !w-[80%] !max-w-[80%] !min-w-[715px] !translate-y-0"
        :style="{ top: getModalTop }"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ productCategoryFullName }}</SDialogTitle>
        </SDialogHeader>
        <div class="grid grid-cols-[repeat(24,minmax(0,1fr))]">
          <div class="col-span-4">
            <img
              v-if="pc"
              :src="productPicture"
              class="!h-[80px] !w-[80px] shrink-0 rounded object-cover"
            />
          </div>
          <div class="col-span-10"></div>
          <div class="col-span-10">
            <div class="grid grid-cols-[repeat(24,minmax(0,1fr))]">
              <div class="key col-span-12">{{ $t("field.availability") }}</div>
              <div class="value col-span-12" data-cy="pc-select-availability">
                {{ availabilities }}
                {{
                  isRetail ? $t("pcselect.left") : $t("pcselect.tickets-left")
                }}
              </div>
            </div>
            <div
              v-if="!isRetail"
              class="grid grid-cols-[repeat(24,minmax(0,1fr))]"
            >
              <div class="key col-span-12">
                {{ $t("field.start-date-time") }}
              </div>
              <div class="value col-span-12">
                {{ formatDateTime(ts?.startDateTime) }}
              </div>
            </div>
            <div
              v-if="!isRetail"
              class="grid grid-cols-[repeat(24,minmax(0,1fr))]"
            >
              <div class="key col-span-12">
                {{ $t("field.end-date-time") }}
              </div>
              <div class="value col-span-12">
                {{ formatDateTime(ts?.endDateTime) }}
              </div>
            </div>
          </div>
        </div>

        <SSeparator class="price-list-divider" />

        <div v-if="pc">
          <div class="price-select-wrapper" data-cy="prices-list-wrapper">
            <div
              class="price-selector flex items-center"
              v-for="(price, index) in sortedPrices(pc.prices)"
              :key="price.uuid"
              ref="pricesRef"
              data-cy="price-item"
            >
              <div class="grid w-full grid-cols-[repeat(24,minmax(0,1fr))]">
                <div class="price-name-wrapper col-span-6">
                  <p class="price-name">
                    {{ priceName(price) }}
                  </p>
                  <p class="price-description">
                    {{
                      truncatePriceDescription(
                        priceDescription(price),
                        priceDescriptionMaxLength
                      )
                    }}
                  </p>
                  <span
                    class="see-more"
                    v-if="
                      priceDescription(price).length > priceDescriptionMaxLength
                    "
                    @click="
                      togglePriceDescription(
                        index,
                        priceDescription(price),
                        priceDescriptionMaxLength
                      )
                    "
                  >
                    {{ t("button.see-more") }}
                  </span>
                </div>
                <div class="col-span-2 flex items-center justify-center">
                  <div class="price-accounting">{{ pc.product.currency }}</div>
                </div>
                <div class="col-span-3 flex items-center justify-center">
                  <div
                    class="price-value-container flex w-full items-center justify-center"
                    v-if="isPriceValueEditable(price)"
                  >
                    <div class="price-accounting editable">
                      {{
                        (
                          getEditedPriceValue(price.uuid) || price.value / 100
                        ).toFixed(2)
                      }}
                    </div>
                    <div class="edit-price-button">
                      <svg
                        @click="editPrice(price.uuid)"
                        data-cy="edit-price-button"
                        class="icon"
                        width="30"
                        height="30"
                        viewBox="0 0 1024 1024"
                        xmlns="http://www.w3.org/2000/svg"
                        data-v-042ca774=""
                      >
                        <path
                          fill="currentColor"
                          d="M832 512a32 32 0 1164 0v352a32 32 0 01-32 32H160a32 32 0 01-32-32V160a32 32 0 0132-32h352a32 32 0 010 64H192v640h640V512z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M469.952 554.24l52.8-7.552L847.104 222.4a32 32 0 10-45.248-45.248L477.44 501.44l-7.552 52.8zm422.4-422.4a96 96 0 010 135.808l-331.84 331.84a32 32 0 01-18.112 9.088L436.8 623.68a32 32 0 01-36.224-36.224l15.104-105.6a32 32 0 019.024-18.112l331.904-331.84a96 96 0 01135.744 0z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <div class="price-accounting" v-else>
                    <p>
                      {{
                        (
                          getCustomizedPriceValue(price.uuid) ??
                          price.value / 100
                        ).toFixed(2)
                      }}
                    </p>

                    <p
                      v-if="getCustomizedPriceValue(price.uuid) !== null"
                      style="font-size: 8px; line-height: 8px"
                    >
                      {{ t("field.customized-price") }}
                    </p>
                  </div>
                </div>
                <div class="col-span-13 flex items-center justify-end gap-4">
                  <div class="flex flex-col items-end justify-center">
                    <div class="counter-controls">
                      <SButton
                        type="button"
                        size="lg"
                        class="w-full price-button"
                        :class="{
                          transparent:
                            pricesCount[price.uuid] ===
                            Math.max(0, getConfirmedOrderLineQuantity(price)),
                        }"
                        :disabled="
                          getCustomizedPriceValue(price.uuid) !== null ||
                          pricesCount[price.uuid] ===
                            Math.max(0, getConfirmedOrderLineQuantity(price))
                        "
                        @click="decrease(price.uuid)"
                      >
                        -
                      </SButton>
                      <input
                        style="position: relative"
                        type="number"
                        class="price-count flex items-center justify-center"
                        v-model.number="pricesCount[price.uuid]"
                        :disabled="getCustomizedPriceValue(price.uuid) !== null"
                        :min="getConfirmedOrderLineQuantity(price)"
                        data-cy="price-quantity-input"
                      />
                      <div>
                        <SButton
                          type="button"
                          size="lg"
                          class="w-full price-button"
                          :class="{
                            transparent: isPriceMaxQuantityReached(price.uuid),
                          }"
                          :disabled="
                            getCustomizedPriceValue(price.uuid) !== null
                          "
                          @click="increase(price.uuid)"
                        >
                          +
                        </SButton>
                        <p
                          class="max-quantity-reached"
                          v-if="isPriceMaxQuantityReached(price.uuid)"
                        >
                          {{ t("pcselect.max-quantity-reached") }}
                        </p>
                      </div>
                    </div>
                    <label
                      class="input-error"
                      v-if="
                        pricesCount[price.uuid] &&
                        !sellOnSoldOut(pc?.prices, pc?.type) &&
                        !sharedStocksWithOverbooking &&
                        pricesCount[price.uuid] > maxInputValue(price.uuid)
                      "
                    >
                      {{
                        t("error.quantiy-less-than") +
                        " " +
                        String(maxInputValue(price.uuid) + 1)
                      }}
                    </label>
                    <label
                      class="input-error"
                      v-if="
                        pricesCount[price.uuid] <
                        getConfirmedOrderLineQuantity(price)
                      "
                      >{{
                        t("error.quantiy-more-than") +
                        " " +
                        String(pricesBookingCount[price.uuid] - 1)
                      }}</label
                    >
                  </div>
                  <div
                    v-if="
                      isRetail &&
                      price.retail[0].type !==
                        RetailOptionType.Default_Retail_Option
                    "
                    class="value"
                  >
                    {{ price.quantity - pricesCount[price.uuid] }}
                    {{ $t("pcselect.left") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SDialogFooter class="gap-3">
          <SButton
            type="button"
            variant="outline"
            class="min-w-[120px]"
            @click="hideDialog"
          >
            {{ t("button.cancel") }}
          </SButton>
          <SButton
            type="button"
            class="min-w-[120px]"
            :loading="isLoading"
            :disabled="isDisabled || !canBook"
            data-cy="confirm-button"
            @click="addPricesToBasket"
          >
            {{ t("button.confirm") }}
          </SButton>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
  <calculator
    v-if="showNewPriceCalculator"
    @confirmNumber="handleCalculatorClick"
    @cancelCalculator="hideCalculator"
    :visible="showNewPriceCalculator"
    :placeHolderValue="pricePlaceHolder"
  ></calculator>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@smeetz/order-store";
import { EventType } from "@smeetz/compliance-common";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SSeparator,
} from "@smeetz/ui-core";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import {
  PCSELECT_IMAGE_HEIGHT,
  PCSELECT_IMAGE_MEASUREMENT_UNIT,
  PCSELECT_IMAGE_WIDTH,
} from "@/constants/style";
import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import ModalModule from "@/store/modules/modal";
import POSConfigModule from "@/store/modules/pos-config";
import { getProductCategoryById } from "@/dexie/product-category";
import { getTimeslotById } from "@/dexie/timeslots";
import { PCSelectModalProps } from "@/constants/modal";
import {
  IProductCategory,
  IProductSlotPrice,
  ISmeetzPicture,
  PCTypes,
  RetailOptionType,
} from "@/types/db/product-category";
import { ITimeSlot } from "@/types/db/timeslots";
import {
  formatAndConvertToLocale,
  formatDateTime,
} from "@/utils/helpers/dates";
import { getLanguage } from "@/utils/helpers/lang";
import { createImageproxyUrl } from "@/utils/helpers/pictures";
import { filterPrices, sellOnSoldOut } from "@/utils/helpers/product-category";
import { processAdditionalEvents } from "@/utils/helpers/compliance";
import { hasAddonsEnabled } from "@/utils/vue-helpers/addons";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import { addToBasket } from "@/utils/vue-helpers/product-category";
import { getPriceNonAutomaticAddons } from "@/utils/vue-helpers/product-slot-state";
import { countPendingOrderQuantity } from "@/utils/vue-helpers/order-stock.helper";
import { getPcStockUnits } from "@/utils/helpers/stock";
import shoppingIcon from "@/assets/images/icons/shopping-icon.png";
import Calculator from "./calculator/Calculator.vue";

export default defineComponent({
  name: ModalComponents.PCSelect,
  components: {
    Calculator,
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SSeparator,
  },
  setup() {
    const { t } = useI18n({});
    // Modal is visible for PCSelect
    const dialogVisible = dialogVisibleForStatus(ModalStatus.PCSelect);
    const pc = ref<IProductCategory | null>(null);
    const ts = ref<ITimeSlot | null>(null);
    const prices = ref<Record<string, IProductSlotPrice>>({});
    const pricesCount = ref<Record<string, number>>({});
    const pricesBookingCount = ref<Record<string, number>>({});
    const isLoading = ref<boolean>(false);
    const showSuccessMessage = ref<boolean>(false);

    const currentLanguage = ref(getLanguage());
    const editingPriceUuid = ref<string>();
    const overridePrices = ref<Record<string, number>>({});
    const showNewPriceCalculator = ref<boolean>(false);
    const pricePlaceHolder = computed(() =>
      editingPriceUuid.value
        ? (prices.value[editingPriceUuid.value]?.value ?? 0) / 100
        : 0
    );
    const priceDescriptionMaxLength = 80;

    const orderStore = useOrderStore();
    const { selectedOrder, selectedOrderSummary: orderSummary } =
      storeToRefs(orderStore);

    const isRetail = computed(() => {
      return pc.value?.type === PCTypes[PCTypes.Retail];
    });

    const sharedStocksWithOverbooking = computed(() => {
      return (
        pc.value?.stockIds &&
        pc.value?.stockIds.length &&
        pc.value.stockIds.includes(-1000)
      );
    });

    // Count pending items from other shared-stock products already in the cart
    const sharedStockPendingCount = computed(() => {
      if (isRetail.value || !pc.value || !ts.value) return 0;
      const totalPending = countPendingOrderQuantity({
        stockIds: pc.value.stockIds ?? [],
        stockUnits: getPcStockUnits(pc.value.stockConsumptions),
        priceUuids: (pc.value.prices ?? []).map((price) => price.uuid),
        timeSlot: {
          id: Number(ts.value.id),
          startDateTime: String(ts.value.startDateTime),
          endDateTime: String(ts.value.endDateTime),
        },
      });
      // totalPending includes items from this product too, which are already
      // tracked by pricesBookingCount/userInputSum, so subtract them
      const thisProductCount = Object.values(pricesBookingCount.value).reduce(
        (a, b) => a + b,
        0
      );
      return Math.max(0, totalPending - thisProductCount);
    });

    const isDisabled = computed(() => {
      if (pc.value && pc.value?.prices && pc.value?.prices.length) {
        for (let i = 0; i < pc.value.prices.length; i++) {
          const price = pc.value.prices[i];
          if (
            pricesCount.value[price.uuid] < getConfirmedOrderLineQuantity(price)
          ) {
            return true;
          }
          if (
            !sellOnSoldOut(pc.value?.prices, pc.value?.type) &&
            !sharedStocksWithOverbooking.value &&
            pricesCount.value[price.uuid] > maxInputValue(price.uuid)
          ) {
            return true;
          }
        }
        return false;
      } else {
        return true;
      }
    });

    onMounted(async () => {
      // process price lookup event
      processAdditionalEvents(EventType.PRICE_LOOKUP).catch((error) => {
        LogService.info("Error processing PRICE LOOKUP event", {
          error,
        });
      });

      await getPC();
    });

    const availabilities = computed(() => {
      // If retail product then sum all prices.quantity of the product category
      // If ticket product then return timeslot.availability
      let avail = 0;
      if (isRetail.value) {
        if (
          pc.value?.prices.length === 1 &&
          pc.value?.prices[0].retail[0].type ===
            RetailOptionType.Default_Retail_Option
        ) {
          // It is retail product but with only one default retail option
          avail = pc.value?.prices[0].quantity;
        } else {
          // this retail has options other than the default one
          pc.value?.prices
            .filter((p) =>
              p.retail.every(
                (r) => r.type !== RetailOptionType.Default_Retail_Option
              )
            )
            .map((p) => p.quantity)
            .forEach((quantity) => (avail = avail + quantity));
        }
      } else {
        avail = (ts.value?.availability || 0) - sharedStockPendingCount.value;
      }

      return avail - userInputSum.value;
    });

    const userInputSum = computed(() => {
      let count = 0;
      if (!pricesCount.value) {
        return 0;
      }

      for (const priceUuid of Object.keys(pricesCount.value)) {
        count = count + pricesCount.value[priceUuid];
      }

      return count;
    });

    async function getPC() {
      const ctx = ModalModule.modalProps.props as
        | PCSelectModalProps
        | undefined;
      const pcId = Number(ctx?.productCategoryId);
      let selectedPC: IProductCategory | undefined;
      try {
        if (!pcId) {
          throw new Error("PCSelect opened without a product category id");
        }
        selectedPC = await getProductCategoryById(pcId);
      } catch (error) {
        NotificationService.showGeneralResponseError();
        LogService.info("Error fetching selected PC", { error });
      }
      if (!selectedPC) {
        return;
      }
      pc.value = filterPrices(selectedPC);

      if (!isRetail.value) {
        ts.value = ctx?.timeslotId
          ? (await getTimeslotById(Number(ctx.timeslotId))) || null
          : null;
        if (!ts.value) {
          LogService.info(
            `Quantity dialog opened without a resolvable timeslot`,
            {
              groupId: POSConfigModule.groupId,
              productCategoryId: pcId,
              timeslotId: ctx?.timeslotId || null,
            },
            LogLevel.Error
          );
        }
      }

      // build price related objects object (count, diff & count in booking)
      for (const price of pc.value?.prices) {
        const priceUuid = price.uuid;
        const currentQuantity = getOrderLineQuantity(price);

        pricesBookingCount.value[priceUuid] = currentQuantity;
        pricesCount.value[priceUuid] = currentQuantity;
        prices.value[priceUuid] = price;
      }
    }

    /**
     * If merch product then return pc.name
     * If ticket product then return pc.product.name + pc.name
     */
    const productCategoryFullName = computed(() => {
      return isRetail.value
        ? pc.value?.nameLocale[currentLanguage.value] || pc.value?.name
        : (pc.value?.product.nameLocale[currentLanguage.value] ||
            pc.value?.product.name ||
            "") +
            " - " +
            (pc.value?.nameLocale[currentLanguage.value] || pc.value?.name);
    });

    /**
     * If merch product then return retailOption name
     * If ticket product then return priceName
     */
    function priceName(price: IProductSlotPrice) {
      // if it is a ticket product then we return the translated name of the price if there is translation, otherwise we return the price name
      // if it is a retail product then we return the translated name of the retail if there is translation, otherwise we return the price name
      // if it is a retail product but there are not retail options, there is only the default retail, then we consider the categoryName name and its translations
      if (!isRetail.value) {
        return price.nameLocale
          ? price.nameLocale[currentLanguage.value] || price.name
          : price.name;
      }

      if (isRetail.value) {
        if (price.retail[0].type !== RetailOptionType.Default_Retail_Option) {
          return price.retail[0].nameLocale
            ? price.retail[0].nameLocale[currentLanguage.value] ||
                price.retail[0].name
            : price.retail[0].name;
        }
        if (price.retail[0].type === RetailOptionType.Default_Retail_Option) {
          return pc.value?.nameLocale[currentLanguage.value] || pc.value?.name;
        }
      }
    }

    /**
     * If merch product then return retailOption description (not available in db yet)
     * If ticket product then return priceDescription
     */
    function priceDescription(price: IProductSlotPrice): string {
      return isRetail.value
        ? ""
        : price.descriptionLocale
        ? price.descriptionLocale[currentLanguage.value] || price.description
        : price.description;
    }

    let productPicture = computed(() => {
      if (pc.value) {
        if (!isRetail.value) {
          return pc.value?.product.pictureUrl;
        } else {
          return (
            createImageproxyUrl(getDefaultRetailOptionSmeetzPicture(pc.value), {
              width: 150 /*Number(PCSELECT_IMAGE_WIDTH)*/,
              height: 150 /*Number(PCSELECT_IMAGE_HEIGHT)*/,
            }) || shoppingIcon
          );
        }
      } else {
        return shoppingIcon;
      }
    });

    const pricesDiff = computed(() => {
      let diffs: Record<string, number> = {};
      if (!pricesCount.value) {
        return {};
      }

      for (const priceUuid of Object.keys(pricesCount.value)) {
        diffs[priceUuid] =
          pricesCount.value[priceUuid] - pricesBookingCount.value[priceUuid];
      }

      return diffs;
    });

    // Checks whether user can book or not
    const canBook = computed(() => {
      // if we have non zero diff on a price, we can book
      for (const priceUuid of Object.keys(pricesDiff.value)) {
        if (pricesDiff.value[priceUuid] !== 0) {
          return true;
        }
      }
      return false;
    });

    function isPriceMaxQuantityReached(priceUuid: string) {
      // Will be used to make sure that we have enough availability for the price the user is trying to increase from
      // If retail product then price.quantity of the current price is the availability
      // If ticket product then we consider timeslot.availability
      let avail = 0;
      if (isRetail.value) {
        avail =
          pc.value?.prices.find((p) => p.uuid === priceUuid)?.quantity || 0;
      } else {
        avail = (ts.value?.availability || 0) - sharedStockPendingCount.value;
      }
      const priceCount = pricesCount.value[priceUuid];
      if (
        priceCount >= avail &&
        !sellOnSoldOut(pc.value?.prices, pc.value?.type) &&
        !sharedStocksWithOverbooking.value
      ) {
        return true;
      }
      return false;
    }

    const getModalTop = computed(() => {
      // Returns how close the modal is to the top of the window
      const defaultTop = 15;
      const toBeDeductedTop = !pc.value ? 0 : pc.value?.prices.length * 3.5;
      const top =
        defaultTop > toBeDeductedTop
          ? defaultTop - toBeDeductedTop + "vh"
          : "0vh";
      return top;
    });

    /**
     * Takes the Product Category and returns RetailOption.SmeetzPictures of the first price for picture display use
     * If the retail has the only one option we show the default option picture
     * Otherwise we show the first retaoil option picture
     * Why we don't always show the default retail option picture?
     * Because when the retail has many options we are not considering the default one inside pc.prices (it will not exist)
     */
    function getDefaultRetailOptionSmeetzPicture(
      pc: IProductCategory
    ): ISmeetzPicture | undefined {
      const smeetzPicture = pc.prices[0].retail[0].smeetzPictures[0];
      return smeetzPicture;
    }

    /**
     * Price value is editable if price.isEditable===1 and there is no pss already created to simplify things in the begining
     */
    function isPriceValueEditable(price: IProductSlotPrice) {
      if (!price.isEditable) return false;
      const orderLines = orderSummary.value?.orderLines.filter(
        (line) => line.priceId === price.uuid
      );
      return !orderLines.length;
    }

    /**
     * Returns the override price (in major units, e.g. 8.99) if the price was
     * manually customized on an existing order line, or null if not. Use
     * non-null check as a boolean guard.
     */
    function getCustomizedPriceValue(priceUuid: string): number | null {
      const line = orderSummary.value?.orderLines.find(
        (l) =>
          l.priceId === priceUuid &&
          !l.parentLineId &&
          l.overridePrice &&
          l.unitPrice !== l.overridePrice
      );
      return line?.overridePrice ?? null;
    }

    function getOrderLineQuantity(price: IProductSlotPrice): number {
      const orderLines = orderSummary.value?.orderLines || [];
      const retailOptionId =
        pc.value?.type === PCTypes[PCTypes.Retail] && price?.retail?.length
          ? price.retail[0].uuid
          : undefined;

      const matchingLines = orderLines.filter(
        (line) =>
          line.priceId === price.uuid &&
          (line.timeSlotId ?? null) === (ts.value?.id ?? null) &&
          (line.retailOptionId ?? null) === (retailOptionId ?? null)
      );

      return matchingLines.reduce((sum, line) => sum + line.quantity, 0);
    }

    /**
     * Returns the net confirmed quantity (sales - cancellations) for a given price
     * across all previous sessions. Matches on categoryId + priceId + timeSlotId + retailOptionId.
     * `CancelLine` quantity is negative, so the sum yields the net quantity.
     */
    function getConfirmedOrderLineQuantity(price: IProductSlotPrice): number {
      if (!selectedOrder.value) return 0;

      const retailOptionId =
        pc.value?.type === PCTypes[PCTypes.Retail] && price?.retail?.length
          ? price.retail[0].uuid
          : undefined;

      const previousLines = selectedOrder.value.previousSessions.flatMap(
        (s) => s.orderLines
      );

      return previousLines
        .filter(
          (line) =>
            line.categoryId === pc.value?.uuid &&
            line.priceId === price.uuid &&
            (line.timeSlotId ?? null) === (ts.value?.id ?? null) &&
            (line.retailOptionId ?? null) === (retailOptionId ?? null)
        )
        .reduce((sum, line) => sum + line.quantity, 0);
    }

    function maxInputValue(priceUuid: string): number {
      return Math.max(availabilities.value + pricesCount.value[priceUuid], 0);
    }

    /**
     * Returns the user-entered override price for this row (major units), or
     * null if the user hasn't overridden it. Local to the modal — only flows
     * to the server when `addToBasket` builds the order line DTO.
     */
    function getEditedPriceValue(priceUuid: string): number | null {
      return overridePrices.value[priceUuid] ?? null;
    }

    const pcselectPictureSize = computed(() => {
      return {
        width: PCSELECT_IMAGE_WIDTH + PCSELECT_IMAGE_MEASUREMENT_UNIT,
        height: PCSELECT_IMAGE_HEIGHT + PCSELECT_IMAGE_MEASUREMENT_UNIT,
      };
    });

    function sortedPrices(prices: IProductSlotPrice[]): IProductSlotPrice[] {
      // for ticket products, sort by priceSort
      if (!isRetail.value) {
        return prices.sort((a, b) => (a.sortPrice ?? 25) - (b.sortPrice ?? 25));
      }
      // for retail products, sort by retail.optionSort
      return prices.sort(
        (a, b) =>
          (a.retail[0].optionSort ?? 25) - (b.retail[0].optionSort ?? 25)
      );
    }

    function togglePriceDescription(
      index: number,
      description: string,
      max: number
    ) {
      const row = this.$refs.pricesRef[index];
      const currentDescription = row.querySelector(".price-description");
      const seeMoreLess = row.querySelector(".see-more");
      if (currentDescription.innerText.length < description.length) {
        currentDescription.innerText = description;
        seeMoreLess.innerText = t("button.see-less");
      } else {
        currentDescription.innerText = truncatePriceDescription(
          description,
          max
        );
        seeMoreLess.innerText = t("button.see-more");
      }
    }

    function truncatePriceDescription(text: string, max: number): string {
      return text.length > max ? text.slice(0, max) + " ..." : text;
    }

    return {
      RetailOptionType,
      t,
      dialogVisible,
      pc,
      ts,
      pricesCount,
      pricesBookingCount,
      pricesDiff,
      canBook,
      isLoading,
      prices,
      currentLanguage,
      isRetail,
      availabilities,
      productCategoryFullName,
      productPicture,
      isPriceMaxQuantityReached,
      priceName,
      priceDescription,
      formatAndConvertToLocale,
      getModalTop,
      editingPriceUuid,
      overridePrices,
      showNewPriceCalculator,
      isPriceValueEditable,
      pricePlaceHolder,
      getEditedPriceValue,
      getCustomizedPriceValue,
      pcselectPictureSize,
      formatDateTime,
      sortedPrices,
      isDisabled,
      maxInputValue,
      sellOnSoldOut,
      sharedStocksWithOverbooking,
      togglePriceDescription,
      truncatePriceDescription,
      priceDescriptionMaxLength,
      hideDialog,
      showSuccessMessage,
      getConfirmedOrderLineQuantity,
    };
  },
  methods: {
    increase(priceUuid: string) {
      if (this.isPriceMaxQuantityReached(priceUuid)) {
        // At this point we wannot increase, so lets just leave
        // TODO: Do something to inform user that this is the max quantity available of the price he is trying increase from
        return;
      }
      const priceCount = this.pricesCount[priceUuid];
      this.pricesCount[priceUuid] = priceCount + 1;
    },
    decrease(priceUuid: string) {
      const priceCount = this.pricesCount[priceUuid];

      if (priceCount === 0) {
        return;
      }
      this.pricesCount[priceUuid] = priceCount - 1;
    },
    async addPricesToBasket() {
      // pc was already checked so cast it directly
      const pc = this.pc as IProductCategory;
      const prices = this.prices as Record<string, IProductSlotPrice>;
      const ts = this.ts as ITimeSlot;
      if (
        !this.isRetail &&
        ts &&
        pc &&
        pc.type === PCTypes[PCTypes.Normal] &&
        ts.categoryId !== pc.id
      ) {
        LogService.info(
          `Timeslot category mismatch in the quantity dialog, blocking the add`,
          {
            groupId: POSConfigModule.groupId,
            pssCategoryId: pc.id,
            tsCategoryId: ts.categoryId,
            tsId: ts.id,
          },
          LogLevel.Error
        );
        NotificationService.showCustomError(
          this.t("error.ticket-slot-mismatch")
        );
        return;
      }

      this.isLoading = true;
      try {
        const pricesDiff = this.pricesDiff;
        const pricesUuids = Object.keys(pricesDiff);
        // check if prices require addons
        let goToAddonsStep = false;
        const pricesToAddToBasket: {
          price: IProductSlotPrice;
          pssQuantity: number;
        }[] = [];
        for (let i = 0; i < pricesUuids.length; i++) {
          const priceUuid = pricesUuids[i];
          const price = prices[priceUuid];
          if (pricesDiff[priceUuid] > 0) {
            pricesToAddToBasket.push({
              price,
              pssQuantity: pricesDiff[priceUuid],
            });
            const addons = await getPriceNonAutomaticAddons(price);
            if (addons.length) {
              goToAddonsStep = true;
            }
          }
        }
        // redirect to addons popup if prices require addons
        if (goToAddonsStep && hasAddonsEnabled.value) {
          ModalModule.setModalProps({
            props: {
              productCategory: pc,
              isRetail: this.isRetail,
              pricesToAddToBasket: pricesToAddToBasket,
              timeslot: ts ?? undefined,
            },
          });
          ModalModule.setStatus(ModalStatus.Hidden);
          await this.$nextTick();
          ModalModule.setStatus(ModalStatus.Addons);
          return;
        }
        // delegate per-row line creation to the shared helper (mirrors
        // addToBasketWithAddons in Addons.vue). Pass `pc` for both
        // selectedProduct and productCategory like SeatingPlan.vue does — the
        // shared helper only reads selectedProduct.prices, available on the PC.
        for (const priceUuid of pricesUuids) {
          const priceDiff = pricesDiff[priceUuid];

          // skip price if it has no diff
          if (priceDiff === 0) {
            continue;
          }

          await addToBasket({
            productCategory: pc,
            price: prices[priceUuid],
            pssQuantity: priceDiff,
            isRetail: this.isRetail,
            timeslot: ts ?? undefined,
            overridePrice: this.getEditedPriceValue(priceUuid) ?? undefined,
          });
        }
      } catch (error) {
        // The order helpers already notified the cashier. Keep the modal open
        // (and re-enabled via `finally`) so the quantities can be adjusted.
        LogService.info("Error adding prices to basket", { error });
        return;
      } finally {
        this.isLoading = false;
      }

      this.dialogVisible = false;
      hideDialog();
      await this.$nextTick();
      if (this.showSuccessMessage) {
        NotificationService.showCustomSuccess(this.t("products.tickets-added"));
      }
    },
    editPrice(priceUuid: string) {
      this.editingPriceUuid = priceUuid;
      this.showNewPriceCalculator = true;
    },
    handleCalculatorClick(newPrice: number) {
      if (!this.editingPriceUuid) {
        return;
      }
      // calculator returns major units; round to 2 decimals
      const rounded = Math.round(newPrice * 100) / 100;
      const catalogMajor =
        (this.prices[this.editingPriceUuid]?.value ?? 0) / 100;
      if (rounded === catalogMajor) {
        // typed back to catalog — clear any prior override for this row
        delete this.overridePrices[this.editingPriceUuid];
      } else {
        this.overridePrices[this.editingPriceUuid] = rounded;
      }
      this.editingPriceUuid = undefined;
      this.hideCalculator();
    },
    hideCalculator() {
      this.showNewPriceCalculator = false;
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.pc-select {
  $button-font-size: 22px;
  $button-height: 65px;

  // .image-wrapper {
  //   height: v-bind("pcselectPictureSize.height");
  //   width: v-bind("pcselectPictureSize.width");
  //   border-radius: 6px;
  //   border: 2px solid $color-light-grey3;
  //   background-color: white;
  //   // Making the image wrapper a flex to center the image in it
  //   display: flex;
  //   flex-direction: column;
  //   // center it vertically if the image is a landscape
  //   justify-content: center;
  //   // center it horizentally if the image is a portrait
  //   align-content: center;

  //   img {
  //     max-height: v-bind("pcselectPictureSize.height");
  //     max-width: v-bind("pcselectPictureSize.width");
  //   }
  // }

  img {
    height: v-bind("pcselectPictureSize.height");
    width: v-bind("pcselectPictureSize.width");
    object-fit: cover;
  }

  .key {
    line-height: 22px;
    letter-spacing: 0px;
    color: black;
    font-weight: 700;
    font-style: normal;
    font-size: 18px;
    text-align: left;
    margin-bottom: 7px;
  }

  .value {
    line-height: 22px;
    letter-spacing: 0px;
    color: black;
    font-weight: 400;
    font-style: normal;
    font-size: 18px;
    text-align: left;
    margin-bottom: 7px;
  }

  .is-fullwidth {
    width: 100%;
    border: none;
  }

  .price-select-wrapper {
    max-height: 450px;
    overflow-y: auto;
  }

  // .grid-content {
  .price-selector {
    padding: 12px 8px;
    // Separate price rows with a hairline divider instead of the legacy grey
    // fill, so the list reads as clean DS rows on the white surface.
    min-height: 8vh;
    background: transparent;
    border-bottom: 1px solid var(--border);
    color: black;

    &:last-child {
      border-bottom: none;
    }

    .price-name-wrapper {
      font-style: normal;
      text-align: left;
      letter-spacing: 0px;
      font-weight: 400;
      font-size: 16px;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .price-name {
        font-weight: 700;
        font-size: 24px;
        line-height: 29px;
        margin-bottom: 8px;
      }
      .price-description {
        word-break: break-word;
      }
      .see-more {
        color: $color-green;
        cursor: pointer;
      }
    }

    .price-accounting {
      font-family: Arial, sans-serif, Arial;
      font-weight: 400;
      font-style: normal;
      font-size: 22px;
      text-align: center;
      line-height: 27px;
      letter-spacing: 0px;
    }

    .price-value-container {
      border: 1px solid $color-light-grey5;
      border-radius: 4px;
      .editable {
        margin-right: 5px;
        padding: 10px;
        border-radius: 4px 0px 0px 4px;
        background-color: $color-light-grey4;
        width: 70%;
      }

      .edit-price-button {
        margin-right: 5px;
        cursor: pointer;
      }
    }

    // Keep the -/input/+ controls one connected unit that fills its grid cell
    // and shrinks on smaller screens instead of overflowing onto the adjacent
    // "left" column. Three equal tracks keep the minus and plus buttons the
    // same width regardless of the plus button's wrapper. Capped so it keeps
    // its original size on wide screens.
    .counter-controls {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      align-items: center;
      width: 100%;
      max-width: 330px;
      > * {
        min-width: 0;
      }
    }
    .price-count {
      // Subtle field so the count stays legible now that the row has no fill.
      background: var(--muted);
      width: 100%;
      min-width: 0;
      min-height: $button-height;
      font-size: $button-font-size;
      text-align: center;
      border: none;
      outline: none;
      cursor: pointer;
    }
    .input-error {
      text-align: center;
      color: red;
    }
    .price-button {
      font-size: $button-font-size;
      height: $button-height;
      width: 100%;
      min-width: 0;
      background-color: $color-green;
    }
    .transparent {
      opacity: 0.5;
    }
    .max-quantity-reached {
      line-height: 14px;
      font-size: 12px;
      text-align: center;
    }
    // Per-option availability: smaller and right-aligned, with a fixed
    // min-width so its box does not change size with the digit count. That
    // keeps the counter in the same position across rows and when the number
    // changes (e.g. 100 -> 99), instead of shifting the buttons.
    .value {
      font-size: 14px;
      margin: 0;
      min-width: 100px;
      text-align: right;
      white-space: nowrap;
    }
  }

  .dialog-footer {
    button {
      width: 25%;
    }
  }

  .price-list-divider {
    margin: 0 0 10px;
  }

  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  input[type="number"],
  input[type="number"]:hover,
  input[type="number"]:focus {
    -moz-appearance: textfield;
    appearance: none;
  }
}
</style>
